/**
 * Custom Identity Hook
 * 
 * This hook wraps the deprecated useIdentity from @workspace/identity-react
 * to provide a stable interface for the application. When @workspace/identity-react
 * is removed in a future release, this hook can be updated to use react-oidc-context
 * without changing the consuming components.
 * 
 * @deprecated The underlying useIdentity hook is deprecated. This wrapper exists
 * to facilitate future migration to react-oidc-context.
 */

import { useIdentity } from '@workspace/identity-react';

export interface AppIdentityState {
  /**
   * Whether the user is currently authenticated
   */
  isAuthenticated: boolean;
  
  /**
   * Whether authentication is in progress
   */
  isLoading: boolean;
  
  /**
   * Whether an authentication error occurred
   */
  isError: boolean;
  
  /**
   * The authentication error, if any
   */
  error?: Error;
  
  /**
   * The authenticated user's information
   * Note: Maps from IdentityUser which uses emailAddress instead of email
   */
  user?: {
    email?: string;
    id?: string;
    organisationId?: string;
  };
  
  /**
   * Get the current access token
   * @returns Promise resolving to the access token string
   */
  getAccessToken: () => Promise<string>;
  
  /**
   * Request re-authentication (e.g., when token is invalid)
   */
  requestReauthentication: () => void;
  
  /**
   * Initiate signin redirect to Identity provider
   */
  signinRedirect: () => Promise<void>;
}

/**
 * Custom hook for identity/authentication in standalone mode.
 * 
 * This hook provides authentication functionality when the app is running
 * outside of an Evo native tab. For apps running inside Evo tabs, use
 * useWidgetIdentity from @workspace/utils-react instead.
 * 
 * @example
 * ```tsx
 * const { isAuthenticated, isLoading, getAccessToken } = useAppIdentity();
 * 
 * if (isLoading) return <Loading />;
 * if (!isAuthenticated) return <LoginRequired />;
 * 
 * const token = await getAccessToken();
 * ```
 * 
 * @returns AppIdentityState - The current authentication state and methods
 */
export const useAppIdentity = (): AppIdentityState => {
  const identity = useIdentity();
  
  return {
    isAuthenticated: identity.isAuthenticated,
    isLoading: identity.isLoading,
    isError: identity.isError,
    error: identity.error,
    user: identity.user ? {
      email: identity.user.emailAddress,
      id: identity.user.id,
      organisationId: identity.user.organisation?.id,
    } : undefined,
    getAccessToken: identity.getAccessToken,
    requestReauthentication: identity.requestReauthentication,
    signinRedirect: () => identity.signinRedirect(),
  };
};

export default useAppIdentity;
